import { createAsyncThunk, createSlice, original } from "@reduxjs/toolkit";
import { type RootState } from "../../app/store";
import { setTaskStatus } from "../../components/layout/SnakebarSlice";
import {
  getSingleSmileData,
  getBioisostericProperties,
} from "./BioisostericApi";
import { isAxiosError } from "axios";
import { errorMessages } from "../../common_variables/ErrorMsgs";
import { setGlobalMsgAlerts } from "../../components/GloablMsgAlerts/globalMsgAlertsSlice";

interface stateType {
  properties: {
    generated_data: any;
    properties_data: any;
    replacement_data: any;
    is_job_data : boolean;
    loading: boolean;
    job_id : string;
    singleSmileData : any;
  };
}

const initialState: stateType = {
  properties: {
    generated_data: null, // set null to check if data is recieved from api
    properties_data: [],
    replacement_data: [],
    is_job_data : false,
    loading: false,
    job_id : "",
    singleSmileData : null,
  },
};

export const fetchBioisostericProperties = createAsyncThunk(
  "bioisoProperties/fetchBioisostericProperties",
  async (task_id: string, { dispatch }) => {
    try {
      const response = await getBioisostericProperties({ task_id });
      return response.data;
    } catch (error: unknown) {
      let msg = "";
      if (isAxiosError(error)) {
        msg = error.response?.data?.detail?.message;
      }
      msg = msg || errorMessages.jobResultError;
      dispatch(
        setGlobalMsgAlerts({
          msg: msg,
          isAlert: true,
          color: "error",
        })
      );
      throw msg;
    }
  }
);

export const fetchSingleSmileData = createAsyncThunk(
  "bioisoProperties/fetchSingleSmileData",
  async (
    {
      smileName,
      properties,
      moleculeCount,
      leavingGroup,
      leavingIdx,
    }: {
      smileName: string;
      properties: string[];
      moleculeCount: Number;
      leavingGroup: string;
      leavingIdx: Number;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      dispatch(
        setTaskStatus({
          status: "pending",
          varient: "info",
          msg: "BioSwap models running...",
          url: "",
          autoHideTime: 5000,
        })
      );

      const response = await getSingleSmileData({
        smileName,
        properties,
        moleculeCount,
        leavingGroup,
        leavingIdx,
      });

      dispatch(
        setTaskStatus({
          status: "success",
          varient: "success",
          msg: "BioSwap request finished successfully.",
          url: "/leader/bioswap/visualize",
          autoHideTime: 3600000,
        })
      );
      return response.data;
    } catch (error: unknown) {
      let msg = "";
      if (isAxiosError(error)) {
        msg = error.response?.data?.detail;
      }
      msg = msg || errorMessages.requestError;
      dispatch(
        setTaskStatus({
          status: "error",
          varient: "error",
          msg: msg,
          url: "",
          autoHideTime: 8000,
        })
      );
      return rejectWithValue(msg);
    }
  }
);

const bioisoSlice = createSlice({
  name: "bioisoProperties",
  initialState,
  reducers: {
    setSingleSmileData(state, action) {
      state.properties.singleSmileData = action.payload;
    },
    updateSmilesById(state, action) {
      const { id } = action.payload;
      const sliceData = original(state.properties);
      state.properties.singleSmileData = {
        ...sliceData.singleSmileData,
        id: id,
        data: sliceData.generated_data[
          sliceData.singleSmileData.evaluationIds[id] - 1
        ],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchBioisostericProperties.fulfilled,
        (state: stateType, action: any) => {
          state.properties.generated_data = action.payload.results;
          state.properties.job_id = action.payload.task_id;
          state.properties.is_job_data = true;
          state.properties.loading = false;
        }
      )
      .addCase(fetchBioisostericProperties.rejected, (state, action: any) => {
        state.properties.loading = false;
      })
      .addCase(fetchBioisostericProperties.pending, (state) => {
        state.properties.loading = true;
      })
      .addCase(
        fetchSingleSmileData.fulfilled,
        (state: stateType, action: any) => {
          state.properties.generated_data = action.payload.generated_data;
          state.properties.properties_data = action.payload.properties_data;
          state.properties.replacement_data = action.payload.replacement_data;
          state.properties.is_job_data = false;
          state.properties.loading = false;
        }
      )
      .addCase(fetchSingleSmileData.rejected, (state, action: any) => {
        state.properties.loading = false;
      })
      .addCase(fetchSingleSmileData.pending, (state) => {
        state.properties.loading = true;
      });
  },
});

export const { setSingleSmileData, updateSmilesById} = bioisoSlice.actions;
export const bioisoProperties = (state: RootState) => state.bioiso;
export default bioisoSlice.reducer;
